import { isCanvasStyleVisible, TAP_LAYER_ATTRIBUTE } from "./canvas-capture";

const MAX_TAP_MOVEMENT_PX = 10;
// A tap whose click never arrives (long press, gesture recognizer) must not stay armable.
const ARMED_CLICK_TIMEOUT_MS = 1000;
const LAYOUT_INTERVAL_MS = 500;

const TOUCH_EVENTS = ["touchstart", "touchmove", "touchend", "touchcancel"];
const POINTER_EVENTS = [
  "pointerdown",
  "pointermove",
  "pointerup",
  "pointercancel",
  "pointerover",
  "pointerout",
  "pointerenter",
  "pointerleave",
  "gotpointercapture",
  "lostpointercapture",
];
const COMPAT_MOUSE_EVENTS = [
  "mousemove",
  "mousedown",
  "mouseup",
  "click",
  "mouseover",
  "mouseout",
  "mouseenter",
  "mouseleave",
  "dblclick",
  "auxclick",
  "contextmenu",
];
// A hovering mouse with no button held, whose mouse events are real rather than emulated.
const HOVER_POINTER_EVENTS = [
  "pointermove",
  "pointerover",
  "pointerenter",
  "pointerout",
  "pointerleave",
];
// Pointer Events still deliver these after a cancelled pointerdown.
const CLICK_EVENTS = ["click", "dblclick", "auxclick", "contextmenu"];
/**
 * Inputs whose cancellation by the game must also cancel the original's default action. Only
 * ones that never block scrolling, so the router listening for them can stay passive otherwise.
 */
export const CANCEL_MIRRORED_EVENTS: readonly string[] = ["contextmenu"];
/** Touch inputs whose cancellation means the game does not want touches to pan or zoom the page. */
export const PAN_BLOCKING_EVENTS: readonly string[] = [
  "touchstart",
  "touchmove",
];
// The hidden switch's own form and focus events, which the game must never see.
const INTERNAL_EVENTS = [
  "input",
  "change",
  "focus",
  "blur",
  "focusin",
  "focusout",
];
export const TAP_INPUT_EVENTS: readonly string[] = [
  ...TOUCH_EVENTS,
  ...POINTER_EVENTS,
  ...COMPAT_MOUSE_EVENTS,
  "wheel",
  ...INTERNAL_EVENTS,
];

type LayerBox = {
  left: number;
  top: number;
  width: number;
  height: number;
  transform: string;
  transformOrigin: string;
  rotate: string;
  scale: string;
  translate: string;
};

type Gesture = {
  startX: number;
  startY: number;
  armed: boolean;
  moved: boolean;
  multiTouch: boolean;
  defaultPrevented: boolean;
  awaitingTouchStart: boolean;
  pointerDownPrevented: boolean;
};

// As in browsers: a cancelled touch gets no emulated mouse events, a cancelled pointerdown a click.
type CompatMouse = "all" | "click" | "none";

/**
 * iOS Safari exposes no vibration API; the only haptic a page can cause is the native tick of an
 * `<input switch>` toggled by a real tap. This lays an invisible label for such a switch over the
 * game canvas and re-dispatches every touch to the canvas, so the game keeps its input while each
 * tap's trusted click lands on the label. The label is only associated with the switch when the
 * game asked for a vibration during that tap, so only those taps tick.
 */
export class TapHaptics {
  private readonly surface: HTMLLabelElement;
  private readonly input: HTMLInputElement;
  private readonly cleanups: (() => void)[] = [];
  private gesture: Gesture | null = null;
  private compatMouse: CompatMouse = "none";
  private disarmTimer: ReturnType<typeof setTimeout> | null = null;
  private layoutKey = "";
  private focusBeforeActivation: Element | null = null;
  private releasedTap = false;
  private releasedTapTimer: ReturnType<typeof setTimeout> | undefined;
  private movingFocus = false;

  /**
   * @param blocksPanning Whether the game already cancelled touches on this canvas; a passive
   * router cannot cancel the originals, so the layer's `touch-action` stops panning instead.
   */
  constructor(
    readonly canvas: HTMLCanvasElement,
    private blocksPanning = false,
  ) {
    const doc = canvas.ownerDocument;
    this.surface = doc.createElement("label");
    this.surface.setAttribute("aria-hidden", "true");
    this.surface.setAttribute(TAP_LAYER_ATTRIBUTE, "");
    this.surface.style.cssText = [
      "position:absolute",
      "display:block",
      "width:1px",
      "height:1px",
      "margin:0",
      "padding:0",
      "border:0",
      "box-sizing:border-box",
      "overflow:hidden",
      "opacity:0",
      "visibility:visible",
      "touch-action:none",
      "user-select:none",
      "-webkit-user-select:none",
      "-webkit-touch-callout:none",
      "-webkit-tap-highlight-color:transparent",
    ].join(";");

    this.input = doc.createElement("input");
    this.input.type = "checkbox";
    this.input.setAttribute("switch", "");
    this.input.tabIndex = -1;
    this.input.id = `jest-haptics-${Math.random().toString(36).slice(2)}`;
    this.input.setAttribute("aria-hidden", "true");
    this.input.style.cssText =
      "position:absolute;left:0;top:0;margin:0;pointer-events:none;appearance:auto;-webkit-appearance:auto;-webkit-tap-highlight-color:transparent";
    this.surface.appendChild(this.input);
    this.setArmed(false);

    canvas.after(this.surface);
    this.layout();
    this.bindEvents();
  }

  get isAttached(): boolean {
    return (
      this.canvas.isConnected &&
      this.canvas.nextElementSibling === this.surface &&
      isCanvasStyleVisible(this.canvas)
    );
  }

  /** Returns false outside a tap and its click, so the request cannot tick. */
  armCurrentTap(): boolean {
    if (this.gesture) {
      this.gesture.armed = true;
      return true;
    }
    if (this.releasedTap) {
      // The label activates its switch only after the click's dispatch, so arming until then ticks.
      this.armReleasedTap();
      return true;
    }
    return false;
  }

  /** Cancels a tick the current tap already asked for. */
  disarmCurrentTap(): void {
    if (this.gesture) {
      this.gesture.armed = false;
    }
    this.setArmed(false);
  }

  destroy(): void {
    this.cleanups.forEach((cleanup) => cleanup());
    this.cleanups.length = 0;
    this.clearDisarmTimer();
    this.setReleasedTap(false);
    this.surface.remove();
  }

  layout(): void {
    if (!this.isAttached) {
      // A layer left over a removed, moved or hidden canvas would keep taking its input.
      this.destroy();
      return;
    }
    const style = this.surface.style;
    const canvasStyle = getComputedStyle(this.canvas);
    style.zIndex = canvasStyle.zIndex;
    style.touchAction = this.blocksPanning
      ? "none"
      : canvasStyle.touchAction || "none";
    style.borderRadius = canvasStyle.borderRadius;
    style.clipPath = canvasStyle.clipPath;
    style.cursor = canvasStyle.cursor;
    style.pointerEvents =
      canvasStyle.pointerEvents === "none" ? "none" : "auto";
    // A fixed canvas stays put while the page scrolls, so the layer must too.
    style.position = canvasStyle.position === "fixed" ? "fixed" : "absolute";

    const next = this.sharesContainingBlock(canvasStyle)
      ? this.mirroredBox(canvasStyle)
      : this.measuredBox();
    const key = Object.values(next).join(",");
    if (key === this.layoutKey) {
      return;
    }
    this.layoutKey = key;
    style.left = `${next.left}px`;
    style.top = `${next.top}px`;
    style.width = `${next.width}px`;
    style.height = `${next.height}px`;
    style.transform = next.transform;
    style.transformOrigin = next.transformOrigin;
    style.rotate = next.rotate;
    style.scale = next.scale;
    style.translate = next.translate;
  }

  // Siblings in one containing block share CSS coordinates, so ancestor transforms hit both alike.
  private sharesContainingBlock(canvasStyle: CSSStyleDeclaration): boolean {
    const parent = this.canvas.offsetParent;
    return (
      parent !== null &&
      parent === this.surface.offsetParent &&
      canvasStyle.position !== "fixed" &&
      canvasStyle.position !== "sticky" &&
      (!canvasStyle.zoom || canvasStyle.zoom === "1")
    );
  }

  private mirroredBox(canvasStyle: CSSStyleDeclaration): LayerBox {
    return {
      left: this.canvas.offsetLeft,
      top: this.canvas.offsetTop,
      width: this.canvas.offsetWidth,
      height: this.canvas.offsetHeight,
      transform: canvasStyle.transform,
      transformOrigin: canvasStyle.transformOrigin,
      rotate: canvasStyle.rotate,
      scale: canvasStyle.scale,
      translate: canvasStyle.translate,
    };
  }

  private measuredBox(): LayerBox {
    const style = this.surface.style;
    const target = this.canvas.getBoundingClientRect();
    const current = this.surface.getBoundingClientRect();
    // Converts screen pixels into the parent's CSS pixels when an ancestor is scaled.
    const scaleX = scaleOf(current.width, this.surface.offsetWidth);
    const scaleY = scaleOf(current.height, this.surface.offsetHeight);
    const left = parseFloat(style.left) || 0;
    const top = parseFloat(style.top) || 0;
    return {
      left: left + (target.left - current.left) / scaleX,
      top: top + (target.top - current.top) / scaleY,
      width: target.width / scaleX,
      height: target.height / scaleY,
      transform: "none",
      transformOrigin: "",
      rotate: "none",
      scale: "none",
      translate: "none",
    };
  }

  private bindEvents(): void {
    const view = this.canvas.ownerDocument.defaultView;
    // iOS only sends a tap's click to an element with its own click listener, never via window.
    this.listen(this.surface, "click", () => {});

    if (view) {
      const abandon = () => this.abandonGesture();
      this.listen(view, "blur", abandon);
      this.listen(view, "pagehide", abandon);
      this.listen(view.document, "visibilitychange", () => {
        if (view.document.hidden) {
          abandon();
        }
      });

      const layout = () => this.layout();
      this.listen(view, "resize", layout);
      this.listen(view, "orientationchange", layout);
      // Sticky canvases move relative to the page while any ancestor scrolls.
      this.listen(view.document, "scroll", layout, true);
      const interval = view.setInterval(layout, LAYOUT_INTERVAL_MS);
      this.cleanups.push(() => view.clearInterval(interval));

      if (typeof view.ResizeObserver === "function") {
        const observer = new view.ResizeObserver(layout);
        observer.observe(this.canvas);
        this.cleanups.push(() => observer.disconnect());
      }
    }
  }

  /** Whether `target` is part of this layer, so its input belongs to {@link handleInput}. */
  owns(target: EventTarget | null): boolean {
    return target instanceof Node && this.surface.contains(target);
  }

  /** Whether the event comes from this layer moving focus, which the game must not see. */
  isOwnFocusChange(event: Event): boolean {
    return this.movingFocus && INTERNAL_EVENTS.includes(event.type);
  }

  /**
   * Forwards a real input event that hit this layer; callers stop it from propagating.
   * Returns false when the game cancelled the forwarded copy.
   */
  handleInput(event: Event): boolean {
    if (TOUCH_EVENTS.includes(event.type)) {
      return this.handleTouch(event as TouchEvent);
    }
    if (POINTER_EVENTS.includes(event.type)) {
      this.handlePointer(event as PointerEvent);
      return true;
    }
    if (event.type === "wheel") {
      return this.handleWheel(event as WheelEvent);
    }
    if (INTERNAL_EVENTS.includes(event.type)) {
      return true;
    }
    return this.handleCompatMouse(event as MouseEvent);
  }

  private handleTouch(event: TouchEvent): boolean {
    if (event.type === "touchstart") {
      const first = event.changedTouches[0];
      if (event.touches.length > 1) {
        this.markMultiTouch();
      } else if (first && !this.gesture?.awaitingTouchStart) {
        this.beginGesture(first.clientX, first.clientY);
      }
      if (this.gesture) {
        this.gesture.awaitingTouchStart = false;
      }
    }

    const gesture = this.gesture;
    if (gesture && event.type === "touchmove") {
      const touch = event.changedTouches[0];
      if (
        touch &&
        Math.hypot(
          touch.clientX - gesture.startX,
          touch.clientY - gesture.startY,
        ) > MAX_TAP_MOVEMENT_PX
      ) {
        gesture.moved = true;
      }
    }

    const notPrevented = this.forward(this.copyTouchEvent(event));
    if (gesture && !notPrevented) {
      gesture.defaultPrevented = true;
    }
    if (!notPrevented && PAN_BLOCKING_EVENTS.includes(event.type)) {
      // Takes effect from the next touch: browsers settle a touch's touch-action when it starts.
      this.blocksPanning = true;
      this.surface.style.touchAction = "none";
    }

    if (event.type === "touchcancel") {
      this.abandonGesture();
      return notPrevented;
    }
    if (event.type !== "touchend" || event.touches.length > 0 || !gesture) {
      return notPrevented;
    }

    this.gesture = null;
    const isTap = !gesture.moved && !gesture.multiTouch;
    // The browser decides whether a drag still clicks; only the game's cancellation suppresses it.
    this.compatMouse = gesture.defaultPrevented
      ? "none"
      : gesture.pointerDownPrevented
        ? "click"
        : "all";
    this.setReleasedTap(isTap);
    if (isTap && gesture.armed) {
      this.armReleasedTap();
    }
    return notPrevented;
  }

  private setReleasedTap(released: boolean): void {
    clearTimeout(this.releasedTapTimer);
    this.releasedTap = released;
    if (released) {
      this.releasedTapTimer = setTimeout(
        () => this.setReleasedTap(false),
        ARMED_CLICK_TIMEOUT_MS,
      );
    }
  }

  private armReleasedTap(): void {
    this.setArmed(true);
    this.disarmTimer = setTimeout(
      () => this.setArmed(false),
      ARMED_CLICK_TIMEOUT_MS,
    );
  }

  private handlePointer(event: PointerEvent): void {
    if (event.type === "pointerdown" && event.pointerType === "touch") {
      if (!event.isPrimary) {
        this.markMultiTouch();
      } else {
        this.beginGesture(event.clientX, event.clientY);
        if (this.gesture) {
          this.gesture.awaitingTouchStart = true;
        }
      }
    }
    const notPrevented = this.forward(
      copyMouseEvent(event, this.retarget(event.relatedTarget)),
    );
    if (event.pointerType === "mouse") {
      // Real mouse events aren't emulated, so they reach the game unless it cancelled the press.
      if (event.type === "pointerdown") {
        this.compatMouse = notPrevented ? "all" : "click";
      } else if (
        HOVER_POINTER_EVENTS.includes(event.type) &&
        event.buttons === 0
      ) {
        this.compatMouse = "all";
      }
    } else if (event.type === "pointerdown" && !notPrevented && this.gesture) {
      this.gesture.pointerDownPrevented = true;
    }
  }

  private handleWheel(event: WheelEvent): boolean {
    return this.forward(
      new WheelEvent(event.type, {
        ...mouseEventInit(event),
        deltaX: event.deltaX,
        deltaY: event.deltaY,
        deltaZ: event.deltaZ,
        deltaMode: event.deltaMode,
      }),
    );
  }

  private handleCompatMouse(event: MouseEvent): boolean {
    if (event.type === "click" && event.target === this.input) {
      // Disabling the switch mid-dispatch could cancel the toggle that produces the tick.
      this.clearDisarmTimer();
      this.disarmTimer = setTimeout(() => this.setArmed(false), 0);
      this.restoreFocus();
      return true;
    }
    const notPrevented = this.forwardCompatMouse(event);
    if (event.type === "click") {
      this.setReleasedTap(false);
      if (!this.input.disabled) {
        this.focusBeforeActivation = this.canvas.ownerDocument.activeElement;
        // Pre-focusing without scrolling makes the activation's own focus a no-op, so no page jump.
        this.moveFocus(() => this.input.focus({ preventScroll: true }));
      }
    }
    return notPrevented;
  }

  private forwardCompatMouse(event: MouseEvent): boolean {
    if (
      this.compatMouse === "none" ||
      (this.compatMouse === "click" && !CLICK_EVENTS.includes(event.type))
    ) {
      return true;
    }
    const notPrevented = this.forward(
      copyMouseEvent(event, this.retarget(event.relatedTarget)),
    );
    // A synthetic mousedown has no focus default, so focus a focusable canvas as the browser would.
    if (
      event.type === "mousedown" &&
      notPrevented &&
      this.canvas.hasAttribute("tabindex")
    ) {
      this.canvas.focus({ preventScroll: true });
    }
    return notPrevented;
  }

  private retarget(target: EventTarget | null): EventTarget | null {
    return this.owns(target) ? this.canvas : target;
  }

  private restoreFocus(): void {
    const previous = this.focusBeforeActivation;
    this.focusBeforeActivation = null;
    if (this.canvas.ownerDocument.activeElement !== this.input) {
      return;
    }
    this.moveFocus(() => {
      if (previous instanceof HTMLElement && previous !== this.input) {
        previous.focus({ preventScroll: true });
      } else {
        this.input.blur();
      }
    });
  }

  private moveFocus(move: () => void): void {
    this.movingFocus = true;
    try {
      move();
    } finally {
      this.movingFocus = false;
    }
  }

  private forward(event: Event): boolean {
    return this.canvas.dispatchEvent(event);
  }

  private copyTouchEvent(event: TouchEvent): TouchEvent {
    const copies = new Map<Touch, Touch>();
    const copy = (touch: Touch): Touch => {
      let result = copies.get(touch);
      if (!result) {
        result = new Touch({
          identifier: touch.identifier,
          target: touch.target === this.surface ? this.canvas : touch.target,
          clientX: touch.clientX,
          clientY: touch.clientY,
          pageX: touch.pageX,
          pageY: touch.pageY,
          screenX: touch.screenX,
          screenY: touch.screenY,
          radiusX: touch.radiusX,
          radiusY: touch.radiusY,
          rotationAngle: touch.rotationAngle,
          force: touch.force,
        });
        copies.set(touch, result);
      }
      return result;
    };
    return new TouchEvent(event.type, {
      bubbles: true,
      cancelable: event.cancelable,
      composed: true,
      view: event.view,
      touches: Array.from(event.touches, copy),
      targetTouches: Array.from(event.targetTouches, copy),
      changedTouches: Array.from(event.changedTouches, copy),
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
    });
  }

  // Browsers fire pointerdown before touchstart, and games often request haptics from either.
  private beginGesture(x: number, y: number): void {
    this.compatMouse = "none";
    this.setReleasedTap(false);
    this.setArmed(false);
    this.gesture = {
      startX: x,
      startY: y,
      armed: false,
      moved: false,
      multiTouch: false,
      defaultPrevented: false,
      awaitingTouchStart: false,
      pointerDownPrevented: false,
    };
  }

  private markMultiTouch(): void {
    if (this.gesture) {
      this.gesture.multiTouch = true;
    }
  }

  private abandonGesture(): void {
    this.gesture = null;
    this.compatMouse = "none";
    this.setArmed(false);
  }

  // `for=""` points the label at no control, so a tap on it cannot toggle the nested switch.
  private setArmed(armed: boolean): void {
    this.clearDisarmTimer();
    this.input.disabled = !armed;
    this.surface.htmlFor = armed ? this.input.id : "";
  }

  private clearDisarmTimer(): void {
    if (this.disarmTimer !== null) {
      clearTimeout(this.disarmTimer);
      this.disarmTimer = null;
    }
  }

  private listen(
    target: EventTarget,
    type: string,
    handler: (event: Event) => void,
    capture = false,
  ): void {
    target.addEventListener(type, handler, { passive: true, capture });
    this.cleanups.push(() =>
      target.removeEventListener(type, handler, { capture }),
    );
  }
}

function scaleOf(screenSize: number, cssSize: number): number {
  return screenSize > 0 && cssSize > 0 ? screenSize / cssSize : 1;
}

// iOS 18.2+ sends click, auxclick and contextmenu as PointerEvents; copies keep that interface.
function copyMouseEvent(
  event: MouseEvent,
  relatedTarget: EventTarget | null,
): MouseEvent {
  if (typeof PointerEvent !== "function" || !(event instanceof PointerEvent)) {
    return new MouseEvent(event.type, mouseEventInit(event, relatedTarget));
  }
  const init: PointerEventInit = {
    ...mouseEventInit(event, relatedTarget),
    pointerId: event.pointerId,
    pointerType: event.pointerType,
    isPrimary: event.isPrimary,
    width: event.width,
    height: event.height,
    pressure: event.pressure,
    tangentialPressure: event.tangentialPressure,
    tiltX: event.tiltX,
    tiltY: event.tiltY,
    twist: event.twist,
  };
  try {
    return new PointerEvent(event.type, {
      ...init,
      coalescedEvents: event.getCoalescedEvents?.() ?? [],
      predictedEvents: event.getPredictedEvents?.() ?? [],
    });
  } catch {
    // Samples are a refinement; an engine that rejects them must not cost the game the event.
    return new PointerEvent(event.type, init);
  }
}

function mouseEventInit(
  event: MouseEvent,
  relatedTarget: EventTarget | null = event.relatedTarget,
): MouseEventInit {
  return {
    view: event.view,
    relatedTarget,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    composed: true,
    clientX: event.clientX,
    clientY: event.clientY,
    screenX: event.screenX,
    screenY: event.screenY,
    movementX: event.movementX,
    movementY: event.movementY,
    button: event.button,
    buttons: event.buttons,
    detail: event.detail,
    altKey: event.altKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    shiftKey: event.shiftKey,
  };
}
